import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, FileText, Trash2, Upload } from 'lucide-react';
import { Card } from '../Shared/Card';
import { Button } from '../Shared/Button';
import { EmptyState } from '../Shared/EmptyState';
import { ConfirmDialog } from '../Shared/ConfirmDialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../Shared/Table';
import { useDocumentStore, type DocumentDto, type DocumentOwnerType } from '../../store/useDocumentStore';

interface DocumentListProps {
  ownerType: DocumentOwnerType;
  ownerId: string;
  /** Render without the surrounding Card (e.g. when already inside a tab/card container). */
  bare?: boolean;
}

function formatFileSize(bytes?: number): string {
  if (!bytes || bytes <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exponent;
  return `${value.toFixed(exponent === 0 ? 0 : 1)} ${units[exponent]}`;
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * Reusable file list for a customer or object. Lists, uploads (proxied through the API),
 * downloads and deletes documents backed by blob storage. Drop in with the owner's type and id.
 */
export function DocumentList({ ownerType, ownerId, bare = false }: DocumentListProps) {
  const { t } = useTranslation(['documents', 'common']);
  const {
    documents,
    loading,
    uploading,
    error,
    fetchDocuments,
    uploadDocument,
    downloadDocument,
    deleteDocument,
  } = useDocumentStore();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingDelete, setPendingDelete] = useState<DocumentDto | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (ownerId) fetchDocuments(ownerType, ownerId);
  }, [ownerType, ownerId, fetchDocuments]);

  const handleFilePicked = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset immediately so picking the same file again re-triggers change.
    event.target.value = '';
    if (!file || !ownerId) return;
    try {
      await uploadDocument(ownerType, ownerId, file);
    } catch {
      // Toast already surfaced by the store.
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete?.id) return;
    setDeleting(true);
    try {
      await deleteDocument(pendingDelete.id);
      setPendingDelete(null);
    } catch {
      // Toast already surfaced by the store.
    } finally {
      setDeleting(false);
    }
  };

  const uploadButton = (
    <>
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleFilePicked}
        aria-hidden="true"
      />
      <Button
        variant="primary"
        size="sm"
        leftIcon={<Upload className="w-3.5 h-3.5" strokeWidth={2} />}
        loading={uploading}
        onClick={() => fileInputRef.current?.click()}
      >
        {t('documents:actions.upload')}
      </Button>
    </>
  );

  const content = (
    <>
      {error ? (
        <EmptyState
          variant="error"
          title={t('documents:errors.fetchFailed')}
          onRetry={() => fetchDocuments(ownerType, ownerId)}
        />
      ) : documents.length === 0 && !loading ? (
        <EmptyState
          icon={<FileText className="w-6 h-6" strokeWidth={2} />}
          title={t('documents:empty.title')}
          description={t('documents:empty.description')}
        />
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>{t('documents:fields.name')}</TableHeader>
              <TableHeader className="hidden sm:table-cell">{t('documents:fields.size')}</TableHeader>
              <TableHeader className="hidden md:table-cell">{t('documents:fields.uploadedAt')}</TableHeader>
              <TableHeader className="text-right">{t('documents:fields.actions')}</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.map((doc) => (
              <TableRow key={doc.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-slate-400 shrink-0" strokeWidth={2} />
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900 truncate">{doc.name}</div>
                      {doc.description && (
                        <div className="text-[12px] text-slate-500 truncate">{doc.description}</div>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="hidden sm:table-cell tabular-nums text-slate-500">
                  {formatFileSize(doc.sizeBytes)}
                </TableCell>
                <TableCell className="hidden md:table-cell tabular-nums text-slate-500">
                  {formatDate(doc.uploadedAt)}
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1 sm:gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      title={t('documents:actions.download')}
                      className="p-1 sm:p-2"
                      onClick={() => downloadDocument(doc)}
                    >
                      <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={2} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      title={t('documents:actions.delete')}
                      className="p-1 sm:p-2"
                      onClick={() => setPendingDelete(doc)}
                    >
                      <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-600" strokeWidth={2} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <ConfirmDialog
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        title={t('documents:delete.title')}
        message={t('documents:delete.message', { name: pendingDelete?.name ?? '' })}
        confirmText={t('documents:actions.delete')}
        variant="danger"
        loading={deleting}
      />
    </>
  );

  if (bare) {
    return (
      <div className="space-y-4">
        <div className="flex justify-end">{uploadButton}</div>
        {content}
      </div>
    );
  }

  return (
    <Card title={t('documents:title')} actions={uploadButton}>
      {content}
    </Card>
  );
}

export default DocumentList;
